import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "spatial-field-planning",
  kind: "domain-vertical",
  title: "Spatial Field & Bed Planning Systems",
  shortTitle: "Spatial Planning",
  status: "draft",
  tier: "we-do",
  seoTitle: "Spatial Field & Bed Planning Systems | GA",
  seoDescription:
    "Hierarchical spatial land modeling, bed geometry, coordinate layouts, and planting density calculations for commercial growing operations.",
  summary:
    "Hierarchical spatial land modeling across parcels, fields, plots, blocks, and beds that turns acreage into an explicit coordinate-driven layout.",
  image: "/images/industries/agriculture-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["agriculture"],
  relatedPostSlugs: [
    "operational-task-management",
    "dashboard-design",
    "from-bed-grid-to-packing-shed",
  ],
  engineering: [
    "Hierarchical spatial modeling: farm parcels, production fields, plots, blocks, beds, and rows",
    "Precise dimension, row-foot, bed-center, and square-footage calculation engines",
    "Interactive 2D grid and canvas-based field layout visualizers with orientation and slope metadata",
    "In-bed spatial arrangement models: multi-row spacing, in-row plant density, and trellis configurations",
    "Micro-location tagging for soil amendment zones, irrigation zones, and high-tunnel boundaries",
    "Multi-tenant land architecture supporting leased parcels, organic buffer zones, and historical plot boundaries",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Land Is a Hierarchical Coordinate System",
    },
    {
      type: "paragraph",
      text: "Farmland is not an unstructured list of named fields. In diversified commercial operations, land is an intricate spatial hierarchy: property parcels divided into operational fields, segmented into blocks, and organized into standardized permanent or semi-permanent raised beds. Without an explicit geometric and spatial data model, calculating linear row feet, seeding quantities, compost volumes, or irrigation runs relies on fragile manual arithmetic.",
    },
    {
      type: "heading",
      level: 2,
      text: "Bridging Spatial Geometry with Agronomic Logic",
    },
    {
      type: "paragraph",
      text: "Useful agricultural software relates physical bed dimensions directly to crop canopy architecture and planting patterns. Connecting bed width, pathway spacing, and row configuration to cultivar growth parameters allows the system to compute precise plant populations, row-cover requirements, and drip-line layouts automatically, establishing an unambiguous geometric ground truth for the farm.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit property boundaries, production zones, bed dimensions, high tunnels, and existing layout sketches to establish spatial requirements.",
      produces: [
        "Farm spatial hierarchy inventory and parcel boundary map",
        "Standardized bed dimension, pathway width, and orientation specifications",
        "Fixed infrastructure mapping (water headers, high tunnels, access roads)",
        "Spatial planning constraint analysis and acreage utilization baseline",
      ],
      fit: {
        good: [
          "Commercial vegetable farms, market gardens, orchards, and research stations needing precise spatial bed management",
          "Operations transitioning from informal field sketches or disparate CAD drawings to an operational planning system",
        ],
        poor: [
          "Broadacre grain monocultures where spatial layout does not involve bed-level geometry or localized succession management",
          "Farms seeking broad GIS satellite mapping without fine-grained field and bed configuration requirements",
        ],
      },
    },
    "initial-design": {
      focus:
        "Architect the relational spatial schema, coordinate systems, bed numbering conventions, and canvas interaction models.",
      produces: [
        "Hierarchical spatial database schema (parcel, field, plot, block, bed, row)",
        "Standardized bed-identification and micro-location tagging system",
        "Coordinate mapping models connecting GPS coordinates to 2D grid systems",
        "Interactive field-map wireframes and bed selection interfaces",
      ],
    },
    "detailed-design": {
      focus:
        "Specify vector canvas rendering engines, linear row-foot calculators, spatial conflict detection rules, and export contracts.",
      produces: [
        "Interactive canvas layout component specifications and zoom behaviors",
        "In-bed plant density and linear-foot calculation algorithms",
        "Buffer-zone and infrastructure collision-detection logic",
        "Spatial data export formats for field implements and operational reports",
      ],
    },
    implementation: {
      focus:
        "Build and deploy the spatial planning application, digitize grower field layouts, and validate bed calculations against physical acreage.",
      produces: [
        "Production spatial field and bed planning application",
        "Fully digitized and calibrated farm layout maps and bed registries",
        "Integrated linear foot and planting density calculation engines",
        "Operational documentation and farm mapping maintenance procedures",
      ],
    },
  },
};
